import { useState, useEffect, useCallback } from 'react';
import { thoughtsService } from '../services/thoughtsService';

export const useBubbleManager = (bubbleSpeed, playWaterDropSound, ensureAudioReady, isPlayerOpen, specificThought = null) => {
  const [activeBubbles, setActiveBubbles] = useState([]);
  const [bubbleHistory, setBubbleHistory] = useState([]);
  const [currentBubbleIndex, setCurrentBubbleIndex] = useState(0);
  const [showMoreBubbles, setShowMoreBubbles] = useState(false);
  const [bubblesShown, setBubblesShown] = useState(0);
  const [allThoughts, setAllThoughts] = useState([]);

  const calculateNonOverlappingPosition = useCallback((currentActiveBubbles) => {
    const bubbleWidth = 200;
    const bubbleHeight = 200;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    let attempts = 0;
    const maxAttempts = 50;

    while (attempts < maxAttempts) {
      const x = (screenWidth - bubbleWidth) / 2; // Center horizontally
      const y = (screenHeight - bubbleHeight) / 2; // Center vertically

      // Check overlap with existing bubbles
      const overlaps = currentActiveBubbles.some(bubble => {
        const distanceX = Math.abs(bubble.position.x - x);
        const distanceY = Math.abs(bubble.position.y - y);

        const minDistance = bubbleWidth * 0.8;
        return distanceX < minDistance && distanceY < minDistance;
      });

      if (!overlaps) {
        return { x, y };
      }

      attempts++;
    }

    // fallback - center with slight random offset
    return {
      x: (screenWidth - bubbleWidth) / 2 + (Math.random() - 0.5) * 100,
      y: (screenHeight - bubbleHeight) / 2 + (Math.random() - 0.5) * 100,
    };
  }, []);

  const handleBubbleComplete = useCallback((bubbleId) => {
    setActiveBubbles(prev => prev.filter(bubble => bubble.id !== bubbleId));
  }, []);

  // Show next bubble in sequence
  const showNextBubble = useCallback(() => {
    // Don't show new bubbles if video player is open
    if (isPlayerOpen) {
      console.log('Video player is open, pausing bubble progression');
      return;
    }

    if (currentBubbleIndex >= allThoughts.length) {
      // All bubbles shown, ask for more
      setShowMoreBubbles(true);
      return;
    }

    const thought = allThoughts[currentBubbleIndex];
    const position = calculateNonOverlappingPosition(activeBubbles);
    
    const newBubble = {
      ...thought,
      position,
      id: thought.id,
      instanceId: `bubble-${thought.id}-${Date.now()}-${Math.random()}`,
      timestamp: Date.now()
    };

    // Add to active bubbles
    setActiveBubbles(prev => [...prev, newBubble]);
    
    // Add to history
    setBubbleHistory(prev => [...prev, newBubble]);
    
    // Ensure audio is ready and play sound
    if (playWaterDropSound && ensureAudioReady) {
      ensureAudioReady().then(() => {
        playWaterDropSound();
      });
    }

    // Start fade-out after 3 seconds and show confirmation dialog
    setTimeout(() => {
      setActiveBubbles(prev => prev.map(bubble => 
        bubble.instanceId === newBubble.instanceId 
          ? { ...bubble, shouldFadeOut: true }
          : bubble
      ));
      
      // Remove bubble completely after fade-out animation (800ms delay)
      setTimeout(() => {
        setActiveBubbles(prev => prev.filter(bubble => bubble.instanceId !== newBubble.instanceId));
        
        // Show confirmation dialog after bubble disappears
        if (currentBubbleIndex + 1 >= allThoughts.length) {
          setShowMoreBubbles(true);
        }
      }, 800);
    }, 3000);

    // Move to next bubble
    setCurrentBubbleIndex(prev => prev + 1);
    setBubblesShown(prev => prev + 1);

    console.log(`Showing bubble ${currentBubbleIndex + 1}: ${thought.thought}`);
  }, [currentBubbleIndex, allThoughts, activeBubbles, calculateNonOverlappingPosition, playWaterDropSound, ensureAudioReady, isPlayerOpen]);

  // Show previous bubble from history
  const showPreviousBubble = useCallback(() => {
    if (bubbleHistory.length === 0) return;

    const previousBubble = bubbleHistory[bubbleHistory.length - 1];
    const position = calculateNonOverlappingPosition(activeBubbles);
    
    const revivedBubble = {
      ...previousBubble,
      position,
      instanceId: `revived-${previousBubble.id}-${Date.now()}-${Math.random()}`,
      timestamp: Date.now()
    };

    // Add to active bubbles
    setActiveBubbles(prev => [...prev, revivedBubble]);
    
    // Ensure audio is ready and play sound
    if (playWaterDropSound && ensureAudioReady) {
      ensureAudioReady().then(() => {
        playWaterDropSound();
      });
    }

    // Start fade-out after 3 seconds
    setTimeout(() => {
      setActiveBubbles(prev => prev.map(bubble => 
        bubble.instanceId === revivedBubble.instanceId 
          ? { ...bubble, shouldFadeOut: true }
          : bubble
      ));
      
      // Remove bubble completely after fade-out animation (800ms delay)
      setTimeout(() => {
        setActiveBubbles(prev => prev.filter(bubble => bubble.instanceId !== revivedBubble.instanceId));
      }, 800);
    }, 3000);

    console.log(`Showing previous bubble: ${previousBubble.text}`);
  }, [bubbleHistory, activeBubbles, calculateNonOverlappingPosition, playWaterDropSound, ensureAudioReady]);

  // Show more bubbles after user confirmation
  const handleShowMoreBubbles = useCallback(async () => {
    setShowMoreBubbles(false);
    
    try {
      // Fetch more random thoughts from Firestore
      const newThoughts = await thoughtsService.getRandomThoughts(10);
      
      const thoughtsWithDelays = newThoughts.map((thought, index) => ({
        ...thought,
        delay: Math.random() * 3000,
      }));
      
      // Add to all thoughts
      setAllThoughts(prev => [...prev, ...thoughtsWithDelays]);
      
      console.log(`Added ${thoughtsWithDelays.length} more thoughts from Firestore`);
    } catch (error) {
      console.error('Failed to fetch more thoughts from Firestore:', error);
      // Fallback: add some default thoughts
      const fallbackThoughts = [
        {
          id: `fallback-${Date.now()}-1`,
          text: 'Keep moving forward',
          youtubeLink: 'https://www.youtube.com/watch?v=ER5Z_MyUkJQ',
          delay: 0
        },
        {
          id: `fallback-${Date.now()}-2`,
          text: 'You have the power to change',
          youtubeLink: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
          delay: 1000
        }
      ];
      setAllThoughts(prev => [...prev, ...fallbackThoughts]);
    }
  }, []);

  // Resume bubble progression
  const resumeBubbleProgression = useCallback(() => {
    setShowMoreBubbles(false);
  }, []);

  // Initialize thoughts from Firestore or use specific thought
  useEffect(() => {
    const initializeThoughts = async () => {
      try {
        if (specificThought) {
          // If a specific thought is provided, use only that thought
          console.log('Using specific thought:', specificThought);
          const thoughtsWithDelays = [{
            ...specificThought,
            delay: 0,
          }];
          setAllThoughts(thoughtsWithDelays);
          setBubblesShown(0);
          setCurrentBubbleIndex(0);
        } else {
          // Load random thoughts from Firestore
          console.log('Fetching thoughts from Firestore...');
          const thoughts = await thoughtsService.getRandomThoughts(20); // Get 20 random thoughts
          
          const thoughtsWithDelays = thoughts.map((thought, index) => ({
            ...thought,
            delay: Math.random() * 3000,
          }));
          
          console.log(`Loaded ${thoughtsWithDelays.length} thoughts from Firestore`);
          setAllThoughts(thoughtsWithDelays);
          setBubblesShown(0);
          setCurrentBubbleIndex(0);
        }
      } catch (error) {
        console.error('Failed to fetch thoughts from Firestore:', error);
        // Fallback to local thoughts if Firestore fails
        const fallbackThoughts = [
          {
            id: 'fallback1',
            thought: 'Every day is a new beginning',
            text: 'Every day is a new beginning', // Keep for backward compatibility
            author: 'Motivational Speaker',
            application: 'Start each day with a positive mindset and fresh perspective',
            summary: 'Every day brings new opportunities and possibilities. Embrace each day as a chance to grow and improve.',
            url: 'https://www.youtube.com/watch?v=ER5Z_MyUkJQ',
            youtubeLink: 'https://www.youtube.com/watch?v=ER5Z_MyUkJQ', // Keep for backward compatibility
            delay: 0
          },
          {
            id: 'fallback2',
            thought: 'You are capable of amazing things',
            text: 'You are capable of amazing things', // Keep for backward compatibility
            author: 'Life Coach',
            application: 'Build self-confidence and believe in your potential',
            summary: 'You have the power within you to achieve incredible things. Trust in your abilities and take action.',
            url: 'https://www.youtube.com/watch?v=9bZkp7q19f0',
            youtubeLink: 'https://www.youtube.com/watch?v=9bZkp7q19f0', // Keep for backward compatibility
            delay: 1000
          }
        ];
        setAllThoughts(fallbackThoughts);
        setBubblesShown(0);
        setCurrentBubbleIndex(0);
      }
    };

    initializeThoughts();

    return () => {
      setActiveBubbles([]);
      setBubbleHistory([]);
    };
  }, [specificThought]); // Only specificThought is needed as dependency since it's the only external value used

  // Show first bubble immediately after initialization
  useEffect(() => {
    if (allThoughts.length > 0 && currentBubbleIndex === 0 && !showMoreBubbles) {
      // Show first bubble immediately
      showNextBubble();
    }
  }, [allThoughts, currentBubbleIndex, showMoreBubbles, showNextBubble]);

  // Auto-show bubbles at regular intervals
  useEffect(() => {
    if (currentBubbleIndex >= allThoughts.length || showMoreBubbles || isPlayerOpen) return;

    const interval = setInterval(() => {
      showNextBubble();
    }, (10 - bubbleSpeed) * 1000); // Each bubble waits (10-bubbleSpeed) seconds

    return () => clearInterval(interval);
  }, [currentBubbleIndex, allThoughts.length, bubbleSpeed, showMoreBubbles, showNextBubble, isPlayerOpen]);

  // Log when bubble progression resumes after video player closes
  useEffect(() => {
    if (!isPlayerOpen && currentBubbleIndex < allThoughts.length && !showMoreBubbles) {
      console.log('Video player closed, resuming bubble progression');
    }
  }, [isPlayerOpen, currentBubbleIndex, allThoughts.length, showMoreBubbles]);

  return {
    activeBubbles,
    showMoreBubbles,
    bubblesShown,
    allThoughts,
    bubbleHistory,
    canGoBack: bubbleHistory.length > 0,
    handleBubbleComplete,
    showPreviousBubble,
    handleShowMoreBubbles,
    resumeBubbleProgression,
    setShowMoreBubbles
  };
};
